/**
 * The doors the model picker offers beside a provider that is not ready
 * (F36). Kept out of the picker component so its rows stay render-only: the
 * picker decides which door applies (`providerInstanceRecovery`), this module
 * performs it.
 *
 * @module providerRecoveryActions
 */
import type {
  ClaudeConnectionState,
  ClaudeNativeAccountBinding,
  DesktopBridge,
  ProviderInstanceId,
  ServerSettings,
} from "@cleo/contracts";
import { runAtomCommand } from "@cleo/client-runtime/state/runtime";

import type { ProviderInstanceRecovery } from "../../providerInstances";
import {
  openWorldRoomProviderKeys,
  openWorldRoomProviders,
} from "../../neucleos/worldViewController";
import { appAtomRegistry } from "../../rpc/atomRegistry";
import { primaryEnvironmentIdAtom } from "../../state/primaryEnvironment";
import { primaryServerSettingsAtom, serverEnvironment } from "../../state/server";

type SettingsSection = Extract<ProviderInstanceRecovery, { kind: "open-settings" }>["section"];

/**
 * Open the Settings section that configures a provider: as the room's
 * Settings surface when a governed room is seated, otherwise the routed page.
 */
export function openProviderSettings(
  section: SettingsSection,
  navigate: ((to: SettingsSection) => void) | undefined,
): void {
  const delivered =
    section === "/settings/provider-keys" ? openWorldRoomProviderKeys() : openWorldRoomProviders();
  if (!delivered) navigate?.(section);
}

export type NativeClaudeVerification = "verified" | "not-verified" | "unavailable";

export interface NativeClaudeVerificationPorts {
  readonly bridge:
    | Pick<DesktopBridge, "connectClaudeAccount" | "getClaudeAccountConnection">
    | undefined;
  readonly binding: ClaudeNativeAccountBinding | undefined;
  /** Ask the server to re-read provider status once Claude has answered. */
  readonly refresh: () => Promise<unknown>;
  readonly wait?: (ms: number) => Promise<void>;
  readonly now?: () => number;
}

const POLL_MS = 500;
// The native check itself gives up after 60 seconds; allow for the ceremony
// around it before treating the attempt as lost.
const GIVE_UP_MS = 120_000;

const settled = (state: ClaudeConnectionState) =>
  state.state !== "authorizing" && state.state !== "verifying";

/**
 * The same Verify the Subscription accounts page offers: the desktop runs one
 * native model request against the account's own Claude home and records the
 * result, then the server re-reads provider status. Only a verified answer
 * for this account's connection counts.
 */
export async function runNativeClaudeVerification(
  ports: NativeClaudeVerificationPorts,
): Promise<NativeClaudeVerification> {
  const { bridge, binding } = ports;
  if (!bridge?.connectClaudeAccount || !bridge.getClaudeAccountConnection || !binding) {
    return "unavailable";
  }
  const wait = ports.wait ?? ((ms) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const now = ports.now ?? Date.now;
  const deadline = now() + GIVE_UP_MS;
  let state: ClaudeConnectionState;
  try {
    state = await bridge.connectClaudeAccount({ mode: "verify", binding });
    while (!settled(state) && now() < deadline) {
      await wait(POLL_MS);
      state = await bridge.getClaudeAccountConnection();
    }
  } catch {
    return "not-verified";
  }
  if (!settled(state)) return "not-verified";
  try {
    await ports.refresh();
  } catch {
    /* the provider stream still converges on its own probe */
  }
  return state.state === "connected" &&
    state.operation === "verify" &&
    state.connectionId === binding.connectionId &&
    state.verification?.outcome === "verified"
    ? "verified"
    : "not-verified";
}

/** The binding Settings stored for a native Claude account instance, if any. */
export function nativeClaudeBinding(
  settings: Pick<ServerSettings, "providerInstances">,
  instanceId: ProviderInstanceId,
): ClaudeNativeAccountBinding | undefined {
  const instance = settings.providerInstances?.[instanceId];
  if (instance?.driver !== "claudeAgent") return undefined;
  return (instance.config as { nativeAccount?: ClaudeNativeAccountBinding } | undefined)
    ?.nativeAccount;
}

/**
 * Verify a native Claude account from the picker. Native accounts live on the
 * primary (desktop) environment, whose settings hold their bindings.
 */
export function verifyNativeClaudeAccount(
  instanceId: ProviderInstanceId,
): Promise<NativeClaudeVerification> {
  const environmentId = appAtomRegistry.get(primaryEnvironmentIdAtom);
  return runNativeClaudeVerification({
    bridge: window.desktopBridge,
    binding: nativeClaudeBinding(appAtomRegistry.get(primaryServerSettingsAtom), instanceId),
    refresh: () =>
      environmentId
        ? runAtomCommand(
            appAtomRegistry,
            serverEnvironment.refreshProviders,
            { environmentId, input: {} },
            { reportFailure: false },
          )
        : Promise.resolve(),
  });
}
